import React, { useState, useEffect, useRef } from 'react';
import ProfileScreen from './ProfileScreen';
import PaywallModal from './PaywallModal';
import { useFreemium } from '../hooks/useFreemium';
import { m, AnimatePresence } from 'motion/react';
import { ArrowLeft, Check, Info, ChevronRight } from 'lucide-react';
import SiteNav from './SiteNav';
import SEO from './SEO';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

// ---------------------------------------------------------------------------
// TYPES
// ---------------------------------------------------------------------------

type OptionColor = 'vert' | 'orange' | 'rouge';

interface SubQuestion {
  trigger: OptionColor[];
  label: string;
  text: string;
  options: { label: string; mod: number; flag?: string }[];
}

interface QuizQuestion {
  id: string;
  bloc: 'A' | 'B';
  dim: 'CP' | 'CA' | 'CN';
  number: string;
  title: string;
  titleEm: string;
  consigne?: string;
  question: string;
  scientific: string;
  options: { label: string; color: OptionColor; score: number }[];
  subQ?: SubQuestion;
  subQ2?: SubQuestion;
}

interface AnswerState {
  score: number;
  color: OptionColor;
  subMod: number;
  flag: string | null;
}

// ---------------------------------------------------------------------------
// QUESTIONS — Protocole 3+3
// ---------------------------------------------------------------------------

const QUESTIONS: QuizQuestion[] = [
  {
    id: 'q1', bloc: 'A', dim: 'CP',
    number: 'Question 1/7',
    title: 'Votre dos se lève-t-il sans signal ?', titleEm: '',
    consigneLabel: 'Faites ce geste',
    consigne: 'Levez-vous lentement, restez debout 3 secondes, puis rasseyez-vous.',
    question: 'Dans les premiers instants du mouvement, qu\'avez-vous ressenti dans votre dos ou votre nuque ?',
    scientific: 'Sous-système passif (disques, ligaments, fascias) — Panjabi, 1992',
    options: [
      { label: 'Rien de particulier, le mouvement était fluide et sans gêne.', color: 'vert', score: 0 },
      { label: 'Une sensation de raideur, de lourdeur ou une impression que le dos "met du temps à se caler".', color: 'orange', score: 1 },
      { label: 'Un pincement, une douleur vive ou la sensation que le dos "flotte" ou "va se dérober".', color: 'rouge', score: 2 },
    ],
    subQ: {
      trigger: ['orange', 'rouge'],
      label: 'Précision importante',
      text: 'Cette sensation est-elle habituelle pour vous, ou plutôt inhabituelle aujourd\'hui\u00a0?',
      options: [
        { label: 'Habituelle depuis plusieurs semaines ou mois', mod: 0 },
        { label: 'Inhabituelle, probablement circonstancielle', mod: -0.5 },
      ],
    },
  },
  {
    id: 'q2', bloc: 'A', dim: 'CA',
    number: 'Question 2/7',
    title: 'Votre dos tient-il sans effort ?', titleEm: '',
    consigneLabel: 'Testez votre dos',
    consigne: 'Asseyez-vous, dos droit, sans appuyer sur le dossier. Tenez 20 secondes.',
    question: 'Comment se sent le bas de votre dos ?',
    scientific: 'Sous-système actif (muscles stabilisateurs) — Panjabi, 1992',
    options: [
      { label: 'Très bien, je tiens la position sans aucun effort ni fatigue.', color: 'vert', score: 0 },
      { label: 'Une fatigue musculaire s\'installe, ou une "barre" commence à apparaître.', color: 'orange', score: 1 },
      { label: 'C\'est douloureux, ou j\'ai une envie irrésistible de m\'affaisser.', color: 'rouge', score: 2 },
    ],
  },
  {
    id: 'q3', bloc: 'A', dim: 'CN',
    number: 'Question 3/7',
    title: 'Votre équilibre est-il stable ?', titleEm: '',
    consigneLabel: 'Testez votre équilibre',
    consigne: 'Debout, levez un pied et tenez 10 secondes. Puis l\'autre côté.',
    question: 'Comment s\'est passé l\'équilibre ?',
    scientific: 'Sous-système neural (proprioception, coordination) — Panjabi, 1992',
    options: [
      { label: 'Stable et facile des deux côtés, sans effort particulier.', color: 'vert', score: 0 },
      { label: 'J\'ai dû faire beaucoup de micro-ajustements, mais j\'ai tenu.', color: 'orange', score: 1 },
      { label: 'Très instable, j\'ai dû reposer le pied ou chercher un appui.', color: 'rouge', score: 2 },
    ],
  },
  {
    id: 'q4', bloc: 'B', dim: 'CN',
    number: 'Question 4/7',
    title: 'Avez-vous vraiment récupéré cette nuit ?', titleEm: '',
    consigneLabel: 'Un instant de mémoire',
    consigne: 'Fermez les yeux 3 secondes. Souvenez-vous du moment de votre réveil ce matin.',
    question: 'Dans quel état étiez-vous en ouvrant les yeux ?',
    scientific: 'Proxy du tonus vagal nocturne — HeartMath Institute, 2015',
    options: [
      { label: 'Reposé·e, frais·che, prêt·e à démarrer.', color: 'vert', score: 0 },
      { label: 'Un peu engourdi·e, sommeil correct mais pas totalement réparateur.', color: 'orange', score: 1 },
      { label: 'Épuisé·e, lourd·e, comme si je n\'avais pas dormi.', color: 'rouge', score: 2 },
    ],
  },
  {
    id: 'q5', bloc: 'B', dim: 'CP',
    number: 'Question 5/7',
    title: 'Portez-vous une fatigue qui ne part pas ?', titleEm: '',
    consigneLabel: 'Un instant de contact',
    consigne: 'Déposez une main sur votre sternum et respirez calmement. Sentez ce que la semaine a déposé là.',
    question: 'Depuis combien de temps portez-vous cette fatigue ou cette pression, même après du repos ?',
    scientific: 'Charge allostatique — McEwen & Stellar, 1993',
    options: [
      { label: 'Ce n\'est pas mon cas, je me sens globalement détendu·e.', color: 'vert', score: 0 },
      { label: 'C\'est passager, quelques jours ou semaines liés à un événement précis.', color: 'orange', score: 1 },
      { label: 'C\'est installé, je traîne cette fatigue depuis plusieurs mois ou années.', color: 'rouge', score: 2 },
    ],
  },
  {
    id: 'q6', bloc: 'B', dim: 'CA',
    number: 'Question 6/7',
    title: 'Votre corps répond-il quand vous lui demandez ?', titleEm: '',
    consigneLabel: 'Une projection rapide',
    consigne: 'Imaginez que vous devez vous lever et aller chercher quelque chose dans la pièce d’à côté.',
    question: 'Quelle est la première réaction de votre corps ?',
    scientific: 'Réserve sympathique vs. effondrement dorsal vagal — Porges, 2011',
    options: [
      { label: 'Prêt·e à agir, mon corps est léger et répond instantanément.', color: 'vert', score: 0 },
      { label: 'Je dois me pousser un peu, il y a une inertie mais l\'énergie vient une fois lancé·e.', color: 'orange', score: 1 },
      { label: 'Corps lourd comme du plomb, sensation d\'épuisement profond, envie de rester immobile.', color: 'rouge', score: 2 },
    ],
    subQ: {
      trigger: ['orange'],
      label: 'Précision',
      text: 'Une fois lancé·e dans une activité, est-ce que l\'énergie revient vraiment\u00a0?',
      options: [
        { label: 'Oui, une fois démarré·e ça repart bien', mod: 0 },
        { label: 'Non, même lancé·e je reste à plat', mod: 0.5 },
      ],
    },
    subQ2: {
      trigger: ['rouge'],
      label: 'Précision',
      text: 'Cette lourdeur est-elle accompagnée d\'une envie de vous isoler, de ne plus parler à personne\u00a0?',
      options: [
        { label: 'Oui, je veux juste disparaître et ne voir personne', mod: 0, flag: 'dorsal' },
        { label: 'Non, c\'est surtout physique et musculaire', mod: 0, flag: 'sympathique' },
      ],
    },
  },
  {
    id: 'q7', bloc: 'B', dim: 'CN',
    number: 'Question 7/7',
    title: 'Votre tête est-elle claire en ce moment ?', titleEm: '',
    consigneLabel: 'Un scan rapide',
    consigne: 'Prenez 3 secondes. Passez en revue vos dernières 24 heures.',
    question: 'Avez-vous eu du mal à vous concentrer, à finir une pensée, ou ressenti un brouillard mental ?',
    scientific: 'Hypervigilance sympathique — Porges, 2011 ; Charge cognitive — Sweller, 1988',
    options: [
      { label: 'Non, ma clarté mentale est bonne, je suis focalisé·e.', color: 'vert', score: 0 },
      { label: 'Oui, par moments, mais ça reste gérable.', color: 'orange', score: 1 },
      { label: 'Oui, de façon persistante — je perds le fil, je relis plusieurs fois.', color: 'rouge', score: 2 },
    ],
  },
];

// ---------------------------------------------------------------------------
// PHRASES MIROIR — 27 profils CN-CP-CA
// ---------------------------------------------------------------------------

const MIRRORS: Record<string, string> = {
  'L-L-L': "Je suis bien là.",
  'L-L-M': "Je suis curieux, qu'est-ce que c'est\u00a0?",
  'L-L-H': "Je ne sais pas pourquoi, mais je n'arrive pas à poser ça.",
  'L-M-L': "Je fais ce qu'il faut, ça tient.",
  'L-M-M': "Je gère, ça va.",
  'L-M-H': "Je fais bonne figure, mais intérieurement ça tourne à plein régime.",
  'L-H-L': "Je continue parce que c'est ce que je fais.",
  'L-H-M': "Je tiens, sans me plaindre.",
  'L-H-H': "Je ne peux pas m'arrêter, même si je le voulais.",
  'M-L-L': "Je suis là mais pas vraiment.",
  'M-L-M': "Je suis fatigué mais je reste aux aguets.",
  'M-L-H': "Je suis à la fois agité et vidé — rien ne m'aide vraiment.",
  'M-M-L': "J'avance, je dose, ça va.",
  'M-M-M': "Le système tourne, je tiens le rythme.",
  'M-M-H': "Je livre, mais quelque chose vérifie en permanence que ça tient.",
  'M-H-L': "Je porte plus que je ne peux, pour les autres.",
  'M-H-M': "Je sens que quelque chose commence à céder.",
  'M-H-H': "Ce que j'aimais faire me coûte maintenant.",
  'H-L-L': "Mon corps a dit stop avant moi.",
  'H-L-M': "Je suis immobile mais je surveille tout.",
  'H-L-H': "L'alarme ne s'éteint pas, même quand tout va bien.",
  'H-M-L': "J'avance, mais chaque pas coûte plus qu'avant.",
  'H-M-M': "Je fais ce qu'il faut. Les résultats sont là. Mais je ne m'habite plus.",
  'H-M-H': "J'exécute. Sans ressentir.",
  'H-H-L': "J'ai sombré, mais personne ne le voit.",
  'H-H-M': "Tout est urgent, tout est lourd, je n'arrive plus à trier.",
  'H-H-H': "Je suis au bout. Tout en même temps.",
};

// ---------------------------------------------------------------------------
// SCORING
// ---------------------------------------------------------------------------

function computeProfile(answers: Record<string, AnswerState>): {
  code: string;
  score: number;
  flag: string | null;
} {
  const get = (id: string) => answers[id];

  const cpR1 = get('q1') ? get('q1').score + (get('q1').subMod ?? 0) : 0;
  const cpR2 = get('q5') ? get('q5').score : 0;
  const caR1 = get('q2') ? get('q2').score : 0;
  const caR2 = get('q6') ? get('q6').score + (get('q6').subMod ?? 0) : 0;
  const cnR1 = get('q3') ? get('q3').score : 0;
  const cnR2 = get('q4') ? get('q4').score : 0;
  const cnR3 = get('q7') ? get('q7').score : 0;

  const cpRaw = Math.max(cpR1, cpR2);
  const caRaw = Math.max(caR1, caR2);
  const cnRaw = Math.max(cnR1, cnR2, cnR3);

  const t = (v: number) => v < 0.75 ? 'L' : v < 1.5 ? 'M' : 'H';
  const CP = t(cpRaw), CA = t(caRaw), CN = t(cnRaw);
  const code = `${CN}-${CP}-${CA}`;

  // Score /100 : charge globale (0 = optimal, 100 = crise)
  const gs = Math.round((cpRaw / 2) * 35 + (caRaw / 2) * 35 + (cnRaw / 2) * 30);
  // Inversion pour compatibilité avec ExperimentationModule (100 = optimal)
  const score = Math.max(10, 100 - gs);

  const flag = get('q6')?.flag ?? null;
  return { code, score, flag };
}

// ---------------------------------------------------------------------------
// BARRE DE PROGRESSION
// ---------------------------------------------------------------------------

function ProgressBar({ answeredCount, total }: { answeredCount: number; total: number }) {
  const pct = (answeredCount / total) * 100;
  return (
    <div className="relative h-1.5 bg-sand/40 w-full overflow-hidden">
      <m.div
        className="absolute inset-y-0 left-0 bg-amber rounded-full"
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      />
      <m.div
        className="absolute inset-y-0 w-8 bg-gradient-to-r from-transparent via-white/50 to-transparent"
        animate={{ left: [`${pct - 8}%`, `${pct + 2}%`, `${pct - 8}%`] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        style={{ opacity: pct > 0 && pct < 100 ? 1 : 0 }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TRANSITION BLOC
// ---------------------------------------------------------------------------

function BlocTransition({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 1800);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <m.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.06)] border border-sand/50 p-8 flex flex-col items-center justify-center min-h-[200px] text-center"
    >
      <m.span
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-[11px] font-bold uppercase tracking-widest text-amber mb-4"
      >
        Bloc B
      </m.span>
      <m.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.4 }}
        className="font-serif text-[22px] text-ink/70 leading-snug"
      >
        Maintenant, 4 questions sur votre journée.
      </m.p>
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// ÉCRAN D'ANALYSE
// ---------------------------------------------------------------------------

function AnalysisScreen({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2800);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[320px] text-center"
    >
      <m.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.7 }}
        className="font-serif text-[20px] text-ink/50 italic max-w-xs leading-relaxed mb-8"
      >
        Lecture de votre système nerveux autonome…
      </m.p>
      <m.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="flex items-center gap-2"
      >
        {[0, 1, 2].map(i => (
          <m.span
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-amber block"
            animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1, 0.8] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}
          />
        ))}
      </m.span>
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// ÉCRAN MIROIR
// ---------------------------------------------------------------------------

function MirrorScreen({
  mirrorText,
  onConfirm,
}: {
  mirrorText: string;
  onConfirm: (yes: boolean, freeText?: string) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [freeText, setFreeText] = useState('');

  return (
    <m.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center text-center py-10 px-4"
    >
      {/* Label */}
      <m.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-[11px] font-bold uppercase tracking-widest text-amber mb-6"
      >
        Votre profil
      </m.span>

      {/* Phrase miroir — sans carte, directement sur le fond */}
      <m.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.7 }}
        className="font-serif text-[1.55rem] md:text-[1.75rem] font-light text-ink leading-snug max-w-xs mb-8"
      >
        <em className="not-italic">&ldquo;</em>{mirrorText}<em className="not-italic">&rdquo;</em>
      </m.p>

      {/* Séparateur */}
      <m.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.7, duration: 0.4 }}
        className="w-10 h-px bg-sand mb-7 origin-center"
      />

      {!showForm ? (
        <>
          <m.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="text-[13px] text-taupe/60 mb-6"
          >
            Cette phrase vous ressemble-t-elle&nbsp;?
          </m.p>

          <m.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05 }}
            className="flex gap-3"
          >
            <button
              onClick={() => onConfirm(true)}
              className="font-sans text-[14px] font-semibold px-7 py-3 rounded-full bg-amber text-paper hover:bg-amber/90 transition-all active:scale-95"
            >
              Oui, c’est ça
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="font-sans text-[14px] font-medium px-7 py-3 rounded-full bg-transparent text-ink/70 border border-sand/80 hover:border-taupe/40 hover:text-ink transition-all active:scale-95"
            >
              Pas tout à fait
            </button>
          </m.div>
        </>
      ) : (
        <m.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm text-left"
        >
          <p className="text-[13px] text-taupe/70 mb-3 text-center">
            Qu’est-ce que vous vous diriez plutôt&nbsp;?
          </p>
          <textarea
            value={freeText}
            onChange={e => setFreeText(e.target.value)}
            placeholder="Notez ce qui vous vient spontanément…"
            rows={3}
            className="w-full text-[14px] text-ink bg-white border border-sand/60 rounded-xl px-4 py-3 resize-none focus:outline-none focus:border-amber/50 focus:ring-1 focus:ring-amber/20 placeholder:text-taupe/35 mb-4"
          />
          <button
            onClick={() => onConfirm(false, freeText)}
            className="w-full font-sans text-[14px] font-semibold py-3.5 rounded-full bg-amber text-paper hover:bg-amber/90 transition-all active:scale-95"
          >
            Obtenir mon exercice personnalisé →
          </button>
          <button
            onClick={() => setShowForm(false)}
            className="w-full mt-2 text-[12px] text-taupe/50 hover:text-taupe/80 transition-colors py-1"
          >
            ← Revenir
          </button>
        </m.div>
      )}
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// CARTE QUESTION
// ---------------------------------------------------------------------------

const COLOR_DOT: Record<OptionColor, string> = {
  vert: 'bg-[#4A7C59]',
  orange: 'bg-amber',
  rouge: 'bg-[#B94040]',
};
const COLOR_SEL: Record<OptionColor, string> = {
  vert: 'border-[#4A7C59] bg-[#4A7C59]/8',
  orange: 'border-amber bg-amber/8',
  rouge: 'border-[#B94040] bg-[#B94040]/8',
};
const CONFIRM_MSG: Record<string, Record<OptionColor, string>> = {
  q1: { vert: '✓ Mouvement fluide noté', orange: '✓ Raideur notée', rouge: '✓ Douleur notée' },
  q2: { vert: '✓ Soutien musculaire noté', orange: '✓ Fatigue posturale notée', rouge: '✓ Douleur posturale notée' },
  q3: { vert: '✓ Équilibre stable noté', orange: '✓ Instabilité légère notée', rouge: '✓ Instabilité marquée notée' },
  q4: { vert: '✓ Bonne récupération notée', orange: '✓ Récupération partielle notée', rouge: '✓ Épuisement matinal noté' },
  q5: { vert: '✓ Charge légère notée', orange: '✓ Charge passagère notée', rouge: '✓ Charge chronique notée' },
  q6: { vert: '✓ Énergie disponible notée', orange: '✓ Inertie légère notée', rouge: '✓ Épuisement profond noté' },
  q7: { vert: '✓ Clarté mentale bonne notée', orange: '✓ Brouillard léger noté', rouge: '✓ Brouillard persistant noté' },
};

interface QuestionCardProps {
  q: QuizQuestion;
  answer: AnswerState | null;
  subAnswers: Record<string, { mod: number; flag: string | null }>;
  onSelect: (qid: string, color: OptionColor, score: number) => void;
  onSubSelect: (qid: string, subN: string, mod: number, flag: string | null) => void;
  isAnswered: boolean;
  dir: 1 | -1;
}

function QuestionCard({ q, answer, subAnswers, onSelect, onSubSelect, isAnswered, dir }: QuestionCardProps) {
  const variants = {
    enter: (d: number) => ({ opacity: 0, x: d * 48, scale: 0.98 }),
    center: { opacity: 1, x: 0, scale: 1 },
    exit: (d: number) => ({ opacity: 0, x: d * -48, scale: 0.98 }),
  };

  const showSub1 = q.subQ && answer && q.subQ.trigger.includes(answer.color);
  const showSub2 = q.subQ2 && answer && q.subQ2.trigger.includes(answer.color);

  return (
    <m.div
      key={q.id}
      custom={dir}
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.06)] border border-sand/50 p-6 md:p-8"
    >
      {/* En-tête — numéro */}
      <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-amber mb-3">
        {q.number}
      </span>

      {/* Consigne — geste à faire, mis en avant */}
      {q.consigne && (
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-widest text-taupe/50 mb-1.5">{q.consigneLabel || 'Avant de répondre'}</p>
          <p className="font-serif text-[20px] md:text-[22px] text-ink leading-snug">{q.consigne}</p>
        </div>
      )}

      {/* Séparateur discret */}
      {q.consigne && <div className="border-t border-sand/60 mb-5" />}

      {/* Question */}
      <p className="text-[15px] font-semibold text-ink mb-5 leading-snug">{q.question}</p>

      {/* Options */}
      <div className="flex flex-col gap-2.5 mb-4">
        {q.options.map((opt, i) => {
          const isSelected = answer?.color === opt.color;
          return (
            <m.button
              key={i}
              whileTap={{ scale: 0.97 }}
              onClick={() => !isAnswered && onSelect(q.id, opt.color, opt.score)}
              aria-pressed={isSelected}
              className={`
                w-full text-left px-4 py-3.5 rounded-xl border-[1.5px] text-[13px] leading-snug
                flex items-start gap-3 transition-all duration-200
                ${isSelected
                  ? `${COLOR_SEL[opt.color]} shadow-sm`
                  : 'bg-sand/15 border-sand/60 text-ink/75 hover:border-amber/40 hover:bg-sand/30'
                }
                ${isAnswered && !isSelected ? 'opacity-40' : ''}
              `}
            >
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${COLOR_DOT[opt.color]}`} aria-hidden="true" />
              <span>{opt.label}</span>
            </m.button>
          );
        })}
      </div>

      {/* Confirmation */}
      <AnimatePresence>
        {answer && (
          <m.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-1.5 text-amber text-[13px] font-semibold mb-4"
          >
            <Check className="w-4 h-4 stroke-[2.5]" />
            {CONFIRM_MSG[q.id]?.[answer.color] ?? '✓ Noté'}
          </m.div>
        )}
      </AnimatePresence>

      {/* Sous-question 1 — CSS grid expand, GPU-composited */}
      {q.subQ && (
        <div
          style={{
            display: 'grid',
            gridTemplateRows: showSub1 ? '1fr' : '0fr',
            opacity: showSub1 ? 1 : 0,
            transition: 'grid-template-rows 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div style={{ overflow: 'hidden' }}>
            <div className="bg-[#EBF2F9] border border-[#2B5F8E]/20 rounded-xl p-4 mb-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#2B5F8E] mb-1">
                ↳ {q.subQ.label}
              </p>
              <p className="text-[13px] font-semibold text-ink mb-3">{q.subQ.text}</p>
              <div className="flex flex-col gap-2">
                {q.subQ.options.map((o, i) => {
                  const isSelected = subAnswers['1']?.mod === o.mod && subAnswers['1'] !== undefined;
                  return (
                    <button
                      key={i}
                      onClick={() => onSubSelect(q.id, '1', o.mod, o.flag ?? null)}
                      className={`
                        text-left text-[13px] px-3 py-3 rounded-lg border-[1.5px] transition-all
                        flex items-center gap-2
                        ${isSelected
                          ? 'border-[#2B5F8E] bg-[#2B5F8E]/8 text-ink'
                          : 'border-[#2B5F8E]/20 bg-white text-ink/70 hover:border-[#2B5F8E]/50'
                        }
                      `}
                    >
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-[#2B5F8E]' : 'bg-sand'}`} />
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sous-question 2 — CSS grid expand, GPU-composited */}
      {q.subQ2 && (
        <div
          style={{
            display: 'grid',
            gridTemplateRows: showSub2 ? '1fr' : '0fr',
            opacity: showSub2 ? 1 : 0,
            transition: 'grid-template-rows 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div style={{ overflow: 'hidden' }}>
            <div className="bg-[#EBF2F9] border border-[#2B5F8E]/20 rounded-xl p-4 mb-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#2B5F8E] mb-1">
                ↳ {q.subQ2.label}
              </p>
              <p className="text-[13px] font-semibold text-ink mb-3">{q.subQ2.text}</p>
              <div className="flex flex-col gap-2">
                {q.subQ2.options.map((o, i) => {
                  const isSelected = subAnswers['2']?.flag === o.flag;
                  return (
                    <button
                      key={i}
                      onClick={() => onSubSelect(q.id, '2', o.mod, o.flag ?? null)}
                      className={`
                        text-left text-[13px] px-3 py-3 rounded-lg border-[1.5px] transition-all
                        flex items-center gap-2
                        ${isSelected
                          ? 'border-[#2B5F8E] bg-[#2B5F8E]/8 text-ink'
                          : 'border-[#2B5F8E]/20 bg-white text-ink/70 hover:border-[#2B5F8E]/50'
                        }
                      `}
                    >
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-[#2B5F8E]' : 'bg-sand'}`} />
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Note scientifique */}
      <div className="flex items-start gap-2 bg-sand/20 rounded-lg px-3 py-2.5 mt-2">
        <Info className="w-3.5 h-3.5 text-taupe/50 flex-shrink-0 mt-0.5" />
        <p className="text-[12px] text-taupe/55 leading-relaxed">{q.scientific}</p>
      </div>
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// COMPOSANT PRINCIPAL
// ---------------------------------------------------------------------------

type Phase = 'quiz' | 'analysis' | 'mirror' | 'profile';

export default function Quiz() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEn = language === 'en';

  const { canEvaluate, nextAvailableAt, countToday, dailyLimit, markEvaluationDone, loading: freemiumLoading } = useFreemium();

  const [phase, setPhase] = useState<Phase>('quiz');
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [subAnswers, setSubAnswers] = useState<Record<string, Record<string, { mod: number; flag: string | null }>>>({});
  const [dir, setDir] = useState<1 | -1>(1);
  const [isReady, setIsReady] = useState(false);
  const [mirrorText, setMirrorText] = useState('');
  const [profileResult, setProfileResult] = useState<{ code: string; score: number; flag: string | null } | null>(null);
  const autoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigating = useRef(false);

  useEffect(() => { const t = setTimeout(() => setIsReady(true), 300); return () => clearTimeout(t); }, []);
  useEffect(() => () => { if (autoTimer.current) clearTimeout(autoTimer.current); }, []);

  const currentQ = QUESTIONS[qIdx];
  const answeredCount = Object.keys(answers).length;

  // Vérifie si toutes les sous-questions requises sont répondues
  const subsDone = (qid: string, color: OptionColor): boolean => {
    const q = QUESTIONS.find(q => q.id === qid);
    if (!q) return true;
    const subs = subAnswers[qid] ?? {};
    if (q.subQ && q.subQ.trigger.includes(color) && !subs['1']) return false;
    if (q.subQ2 && q.subQ2.trigger.includes(color) && !subs['2']) return false;
    return true;
  };

  const handleSelect = (qid: string, color: OptionColor, score: number) => {
    if (autoTimer.current) clearTimeout(autoTimer.current);

    const q = QUESTIONS.find(q => q.id === qid)!;
    const needsSub = (q.subQ && q.subQ.trigger.includes(color)) || (q.subQ2 && q.subQ2.trigger.includes(color));

    setAnswers(prev => ({
      ...prev,
      [qid]: { score, color, subMod: 0, flag: null },
    }));
    // Reset sub-answers for this question
    setSubAnswers(prev => ({ ...prev, [qid]: {} }));

    if (!needsSub) {
      autoTimer.current = setTimeout(() => advance(qid, { score, color, subMod: 0, flag: null }), 900);
    }
  };

  const handleSubSelect = (qid: string, subN: string, mod: number, flag: string | null) => {
    setSubAnswers(prev => {
      const updated = { ...prev, [qid]: { ...(prev[qid] ?? {}), [subN]: { mod, flag } } };
      // Recalculate subMod
      const allSubs = Object.values(updated[qid] ?? {});
      const subMod = allSubs.reduce((acc, s) => acc + s.mod, 0);
      const flags = allSubs.map(s => s.flag).filter(Boolean);
      const resolvedFlag = flags[0] ?? null;

      setAnswers(prev2 => ({
        ...prev2,
        [qid]: { ...prev2[qid], subMod, flag: resolvedFlag },
      }));

      // Check if all subs done
      const color = answers[qid]?.color;
      if (color) {
        const q = QUESTIONS.find(q => q.id === qid)!;
        const sub1Done = !q.subQ || !q.subQ.trigger.includes(color) || !!updated[qid]['1'];
        const sub2Done = !q.subQ2 || !q.subQ2.trigger.includes(color) || !!updated[qid]['2'];
        if (sub1Done && sub2Done) {
          const finalAnswer: AnswerState = { ...answers[qid], subMod, flag: resolvedFlag };
          autoTimer.current = setTimeout(() => advance(qid, finalAnswer), 700);
        }
      }
      return updated;
    });
  };

  const advance = (qid: string, finalAnswer: AnswerState) => {
    const nextIdx = qIdx + 1;
    const allAnswers = { ...answers, [qid]: finalAnswer };

    if (nextIdx >= QUESTIONS.length) {
      // Fin du quiz
      const result = computeProfile(allAnswers);
      setProfileResult(result);
      const mirror = MIRRORS[result.code] ?? '';
      setMirrorText(mirror);
      markEvaluationDone(); // Marque l'évaluation du jour comme effectuée
      setPhase('analysis');
      return;
    }

    setDir(1);
    setQIdx(nextIdx);
  };

  const handleAnalysisDone = () => {
    setPhase('mirror');
  };

  const handleMirrorConfirm = (_yes: boolean, freeText?: string) => {
    if (!profileResult) return;
    if (_yes) {
      // Oui, c'est ça → afficher l'écran de profil individuel
      setPhase('profile');
    } else {
      // Pas tout à fait → redirection vers experimentation avec texte libre
      if (navigating.current) return;
      navigating.current = true;
      navigate(
        isEn
          ? `/experimentation/${profileResult.code}?score=${profileResult.score}`
          : `/fr/experimentation/${profileResult.code}?score=${profileResult.score}`,
        { state: { scores: answers, mirrorConfirmed: false, mirrorFreeText: freeText ?? '', flag: profileResult.flag } }
      );
    }
  };

  const handleProfileCTA = () => {
    if (!profileResult || navigating.current) return;
    navigating.current = true;
    navigate(
      isEn
        ? `/experimentation/${profileResult.code}?score=${profileResult.score}`
        : `/fr/experimentation/${profileResult.code}?score=${profileResult.score}`,
      { state: { scores: answers, mirrorConfirmed: true, flag: profileResult.flag } }
    );
  };

  const handleBack = () => {
    if (autoTimer.current) clearTimeout(autoTimer.current);
    if (qIdx === 0) {
      navigate(isEn ? '/' : '/fr/');
    } else {
      setDir(-1);
      setQIdx(i => i - 1);
    }
  };

  // Paywall freemium : géré en overlay dans le return principal

  // Questionnaire interactif : non indexé pour l'instant (le build SSG n'en rend
  // que le squelette). Balises rendues aussi dans le squelette, seul état du build.
  const seo = (
    <SEO
      title="Bilan somatique — Amargi"
      description="Évaluez votre système nerveux en 7 questions. Obtenez votre profil somatique personnalisé et une micro-pratique immédiate, gratuit et sans inscription."
      path="/evaluation"
      noindex
    />
  );

  // Skeleton
  if (!isReady) {
    return (
      <div className="min-h-[100dvh] bg-paper flex flex-col items-center justify-center gap-6 px-4">
        {seo}
        <div className="w-full max-w-lg">
          <div className="flex items-center justify-center gap-2 mb-8">
            {[...Array(7)].map((_, i) => (
              <div key={i} className={`h-2 rounded-full bg-sand/50 animate-pulse ${i === 0 ? 'w-6' : 'w-2'}`} />
            ))}
          </div>
          <div className="bg-white rounded-[20px] shadow-sm border border-sand/30 p-8 space-y-5">
            <div className="h-3 w-24 bg-sand/60 rounded-full animate-pulse" />
            <div className="h-7 w-full bg-sand/40 rounded-full animate-pulse" />
            <div className="h-7 w-3/4 bg-sand/40 rounded-full animate-pulse" />
            <div className="h-4 w-2/3 bg-sand/30 rounded-full animate-pulse" />
            <div className="flex flex-col gap-2 pt-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 rounded-xl bg-sand/30 animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-paper text-ink font-sans flex flex-col">
      {seo}

      {/* Paywall freemium : overlay bloquant rendu dès que la limite est atteinte */}
      {!freemiumLoading && !canEvaluate && (
        <PaywallModal
          nextAvailableAt={nextAvailableAt}
          countToday={countToday}
          dailyLimit={dailyLimit}
          onBack={() => navigate(isEn ? '/' : '/fr/')}
        />
      )}

      {/* Top bar fixe — masqué sur mirror et profile pour continuité visuelle */}
      {(phase === 'quiz' || phase === 'analysis') && (
        <div className="fixed top-0 w-full z-50">
          <SiteNav logoVariant="primary" />
          <ProgressBar answeredCount={answeredCount} total={QUESTIONS.length} />
        </div>
      )}

      <main className={`flex-grow flex flex-col items-center justify-center px-4 py-8 ${
        phase === 'quiz' || phase === 'analysis' ? 'pt-14 sm:pt-22' : 'pt-8'
      }`}>
        <div className="w-full max-w-lg">

          {/* Dots de progression — uniquement pendant le quiz */}
          {phase === 'quiz' && (
            <div className="flex items-center justify-center gap-2 mb-6">
              {QUESTIONS.map((_, i) => (
                <m.div
                  key={i}
                  animate={{
                    width: i === qIdx ? 24 : 8,
                    backgroundColor: i < qIdx
                      ? 'var(--color-amber)'
                      : i === qIdx
                      ? 'var(--color-amber)'
                      : 'var(--color-sand)',
                    opacity: i > qIdx ? 0.4 : 1,
                  }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  className="h-2 rounded-full"
                />
              ))}
            </div>
          )}

          {/* Contenu selon la phase */}
          <AnimatePresence mode="wait" custom={dir}>
            {phase === 'analysis' && (
              <AnalysisScreen key="analysis" onDone={handleAnalysisDone} />
            )}

            {phase === 'mirror' && (
              <MirrorScreen
                key="mirror"
                mirrorText={mirrorText}
                onConfirm={handleMirrorConfirm}
              />
            )}

            {phase === 'profile' && profileResult && (
              <ProfileScreen
                key="profile"
                code={profileResult.code}
                score={profileResult.score}
                onCTA={handleProfileCTA}
              />
            )}

            {phase === 'quiz' && currentQ && (
              <QuestionCard
                key={currentQ.id}
                q={currentQ}
                answer={answers[currentQ.id] ?? null}
                subAnswers={subAnswers[currentQ.id] ?? {}}
                onSelect={handleSelect}
                onSubSelect={handleSubSelect}
                isAnswered={!!answers[currentQ.id] && subsDone(currentQ.id, answers[currentQ.id]?.color)}
                dir={dir}
              />
            )}
          </AnimatePresence>

          {/* Bouton retour */}
          {phase === 'quiz' && (
            <div className="mt-5 flex justify-start">
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 text-[13px] text-taupe/55 hover:text-ink transition-colors px-3 py-2 rounded-lg hover:bg-sand/30"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Retour</span>
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
